import {
  AnalyticReportAccessDTO,
  AxiosInstanceEnum,
  ISearchUsersParameters,
  LoginService,
  User,
  UserAnalyticAccessDTO,
  UserCompressed,
  UserLoginDetail,
  UserLoginDetailApiVersionEnum,
  UserQuota,
  UserService,
  WorkHoursWeekDto,
} from '@jooxter/api';
import { useStore } from '../../store';
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { UserQueryKeys } from '../queryKeys';
import { UserStaleTimeEnum } from '../staleTimes';
import { useCallback, useEffect, useMemo } from 'react';
import { flatMap, uniqBy } from 'lodash-es';
import { fetchAuthSession } from 'aws-amplify/auth';
import { AxiosError } from 'axios';
import { ErrorEnum } from '@jooxter/ui';
import { useAuthContext } from '../../auth';

export const useFetchUser = (): {
  user: User | undefined;
  error?: AxiosError<User, null> | null;
  isLoading: boolean;
} => {
  const [token, setEmail, resetFilterFromAndTo] = useStore((state) => [
    state.token,
    state.setEmail,
    state.resetFilterFromAndTo,
  ]);
  const fetchUser = useCallback(() => {
    return UserService.getMe();
  }, []);
  const { logout } = useAuthContext();

  // isLoading stay to false while refetching
  // either from "refetchInterval"
  // or when refetching on window focus and after staleTime
  // Note that isLoading will be true when refetching and the token has changed
  // (ie after logout/login or after refreshing the token)
  // @FIX avoid UI flickering when refreshing the token
  // https://jooxter.atlassian.net/browse/ND-6061
  const {
    data: user,
    error,
    isLoading,
  } = useQuery<User | undefined, AxiosError<User, null>>({
    queryKey: [UserQueryKeys.GetMe, token],
    queryFn: fetchUser,
    staleTime: UserStaleTimeEnum.GetMe,
    enabled: !!token,
    placeholderData: (previousData) => (token ? keepPreviousData(previousData) : undefined),
    retry: 3,
  });

  useEffect(() => {
    if (error?.status === ErrorEnum.NOT_AUTHORIZED || error?.status === ErrorEnum.FORBIDDEN) {
      logout();
    }
  }, [error, logout]);

  useEffect(() => {
    fetchAuthSession();

    if (user?.email) {
      setEmail(user.email);
    }
  }, [user, setEmail]);

  useEffect(() => {
    if (user?.timezone) {
      resetFilterFromAndTo(user.timezone);
    }
  }, [user?.timezone, resetFilterFromAndTo]);

  return { user, error, isLoading };
};

export const useFetchUserById = (
  id: number | undefined | null
): { user: User | null | undefined; isLoading: boolean } => {
  const fetchUser = useCallback(() => {
    if (id) {
      return UserService.getUser(id);
    }

    return Promise.resolve(null);
  }, [id]);

  const { data: user, isLoading } = useQuery({
    queryKey: [UserQueryKeys.GetUserById, id],
    queryFn: () => fetchUser(),
    staleTime: UserStaleTimeEnum.GetUserById,
  });

  return { user, isLoading };
};

export const useFetchUsersSearch = (
  options: ISearchUsersParameters,
  enabled = true
): {
  users: UserCompressed[] | undefined;
  isLoading: boolean;
  isError: boolean;
  fetchNextPage: (options?: FetchNextPageOptions | undefined) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<
        {
          data: UserCompressed[];
          nextPage: string;
        },
        unknown
      >
    >
  >;
  hasNextPage?: boolean;
  lastPage: UserCompressed[];
  isFetching: boolean;
} => {
  const fetchUsers = async ({ pageParam = '' }) => {
    const newOptions = { ...options };
    if (pageParam) {
      newOptions.page = pageParam;
    }

    return await UserService.getUsersWithPagination(newOptions);
  };

  const { data, isLoading, isError, isFetching, isRefetching, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [UserQueryKeys.GetUsersSearch, options],
    queryFn: fetchUsers,
    getNextPageParam: (lastPage: { nextPage?: string; data: UserCompressed[] }) => {
      // must return undefined if it's the last page
      // lastPage.nextPage is ''
      return lastPage.nextPage?.length && lastPage.nextPage.length > 0 ? lastPage.nextPage : undefined;
    },
    initialPageParam: '',
    staleTime: UserStaleTimeEnum.GetUsersSearch,
    placeholderData: keepPreviousData,
    enabled,
  });

  const users = useMemo(() => uniqBy(flatMap(data?.pages.map((page) => page.data) ?? []), 'id'), [data?.pages]);
  const lastPage = useMemo(() => data?.pages.at(-1)?.data ?? [], [data?.pages]);

  return {
    users,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    lastPage,
    isFetching: isRefetching || isFetching,
  };
};

export const useFetchLoginDetails = (
  email?: string
): { loginDetails?: UserLoginDetail | null; isPending: boolean; isLoading: boolean } => {
  const [setAxiosInstance] = useStore((state) => [state.setAxiosInstance]);

  const fetchLoginDetails = () => {
    if (email) {
      return LoginService.getUserLoginDetails(email);
    }

    return Promise.reject(null);
  };

  const {
    data: loginDetails,
    isPending,
    isLoading,
  } = useQuery({
    queryKey: [UserQueryKeys.GetUserLoginDetails, email],
    queryFn: () => fetchLoginDetails(),
    staleTime: UserStaleTimeEnum.GetUserLoginDetails,
    enabled: !!email,
  });

  useEffect(() => {
    if (loginDetails?.apiVersion === UserLoginDetailApiVersionEnum.V4) {
      setAxiosInstance(AxiosInstanceEnum.axiosInstanceV4);
    }
  }, [loginDetails, setAxiosInstance]);

  return { loginDetails, isPending, isLoading };
};

export const useFetchUserPreferences = (id?: number) => {
  const fetchUserPreferences = useCallback(() => {
    if (id) {
      return UserService.getPreferences(id);
    }

    return Promise.resolve(null);
  }, [id]);

  const { data: preferences } = useQuery({
    queryKey: [UserQueryKeys.GetMyPreferences, id],
    queryFn: () => fetchUserPreferences(),
    staleTime: UserStaleTimeEnum.GetMyPreferences,
  });

  return { preferences };
};

export const useFetchUserNotifications = (id?: number) => {
  const fetchUserNotifications = useCallback(() => {
    if (id) {
      return UserService.getNotifications(id);
    }

    return Promise.resolve(null);
  }, [id]);

  const { data: notifications } = useQuery({
    queryKey: [UserQueryKeys.GetMyNotifications, id],
    queryFn: () => fetchUserNotifications(),
    staleTime: UserStaleTimeEnum.GetMyNotifications,
  });

  return { notifications };
};

export const useFetchUserIcsCalendar = () => {
  const fetchUserIcsCalendar = () => UserService.postUserIcsCalendar();

  const { data: calendar } = useQuery({
    queryKey: [UserQueryKeys.PostUserIcsCalendar],
    queryFn: () => fetchUserIcsCalendar(),
    staleTime: UserStaleTimeEnum.PostUserIcsCalendar,
  });

  return { calendar };
};

export const useFetchWeek = (id?: number): { week: WorkHoursWeekDto | undefined | null; isLoading: boolean } => {
  const fetchWeek = useCallback(() => {
    if (id) {
      return UserService.getTypicalWeek(id);
    }

    return Promise.resolve(null);
  }, [id]);

  const { data: week, isLoading } = useQuery({
    queryKey: [UserQueryKeys.GetTypicalWeek, id],
    queryFn: () => fetchWeek(),
    staleTime: UserStaleTimeEnum.GetTypicalWeek,
  });

  return { week, isLoading };
};

export const useInvalidatePersonalUser = () => {
  const queryClient = useQueryClient();
  const [token] = useStore((state) => [state.token]);

  const invalidatePersonalUser = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [UserQueryKeys.GetMe, token],
    });
  }, [queryClient, token]);

  return { invalidatePersonalUser };
};

export const useFetchUserResourceQuota = (
  userId: number | undefined | null,
  resourceId: number | undefined | null,
  from: string,
  to: string,
  enabled = true
): { quota: UserQuota | null | undefined; isLoading: boolean } => {
  const fetchUserResourceQuota = useCallback(() => {
    if (userId && resourceId) {
      return UserService.getResourceQuota(userId, resourceId, from, to);
    }

    return Promise.resolve(null);
  }, [userId, resourceId, from, to]);

  const { data: quota, isLoading } = useQuery({
    queryKey: [UserQueryKeys.GetResourceQuota, userId, resourceId, from, to],
    queryFn: () => fetchUserResourceQuota(),
    staleTime: UserStaleTimeEnum.GetResourceQuota,
    enabled,
  });

  return { quota, isLoading };
};

export const useInvalidateResourceQuotaQueries = () => {
  const queryClient = useQueryClient();

  const invalidateResourceQuotaQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [UserQueryKeys.GetResourceQuota],
    });
  }, [queryClient]);

  return { invalidateResourceQuotaQueries };
};

export const useFetchUserAnalytics = (
  userId?: number,
  enabled = true
): {
  userAnalytics: UserAnalyticAccessDTO | undefined;
  isLoading: boolean;
  isError: boolean;
} => {
  const fetchAnalytics = () => UserService.getUserAnalytics();

  const {
    data: userAnalytics,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [UserQueryKeys.GetUserAnalytics, userId],
    queryFn: () => fetchAnalytics(),
    placeholderData: keepPreviousData,
    staleTime: UserStaleTimeEnum.GetUserAnalytics,
    enabled: !!(userId && enabled),
  });

  return { userAnalytics, isLoading, isError };
};

export const useFetchUserAnalyticReportAccess = (
  userId?: number,
  enabled = true
): {
  userAnalyticAccess: AnalyticReportAccessDTO[] | undefined;
  isLoading: boolean;
  isError: boolean;
} => {
  const fetchAnalytics = () => UserService.getUserAnalyticAccess();

  const {
    data: userAnalyticAccess,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [UserQueryKeys.GetAnalyticReport, userId],
    queryFn: () => fetchAnalytics(),
    placeholderData: keepPreviousData,
    staleTime: UserStaleTimeEnum.GetAnalyticReport,
    enabled: !!(userId && enabled),
  });

  return { userAnalyticAccess, isLoading, isError };
};
